import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CognitoUser, MfaUpdateAuthRequestData } from '@mobi/api-types'
import { cognitoUserQueryKey } from '@core/Data/Account/useCognitoUser'
import { post } from '@core/Services/ApiService'
import { throwApiError } from '../Helpers/utils'
import {
  CognitoUnauthorizedError,
  isCognitoUserAuthenticated,
} from './useCognitoUserIsAuthenticated'

export function useMfaUpdateAuthMutation() {
  const queryClient = useQueryClient()
  const queryKey = cognitoUserQueryKey()

  return useMutation({
    mutationFn: async (body: MfaUpdateAuthRequestData) => {
      const isAuthenticated = await isCognitoUserAuthenticated()

      if (!isAuthenticated) {
        throw new CognitoUnauthorizedError()
      }

      try {
        return await post<void>({
          url: '/api/mfa/update-auth-factor',
          body,
        })
      } catch (error) {
        await throwApiError(error)
      }
    },
    onMutate: async (newPreferences: MfaUpdateAuthRequestData) => {
      await queryClient.cancelQueries({ queryKey })

      const previousData = queryClient.getQueryData<CognitoUser>(queryKey)

      queryClient.setQueryData(queryKey, (oldData: CognitoUser) => ({
        ...oldData,
        preferredFactor: newPreferences.preferredFactor,
      }))

      return { previousData }
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<CognitoUser>(queryKey, context.previousData)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}
